import React, { Component } from "react";
import {
  formatIarFeeRate,
  normalizeIarFeeTiered,
  setTieredRate,
} from "./iarFeeTieredUtils";
import { isValidIarPercentage } from "./ispBusinessRules";
import {
  ELEMENTS_INITIAL_FEE_STAGE_HEADING,
  getElementsPathFeeStageHeading,
  isElementsStrategyName,
} from "./elementsFeeUtils";
import {
  formatFundingAmount,
  getIspFundingThreshold,
} from "./ispFundingThresholds";
import {
  getIarFeeScheduleRows,
  getIarFeeSchedules,
} from "./iarFeeScheduleUtils";

class ISPFeeSchedule extends Component {
  isManagedMoneyMarketStrategy = () =>
    String(this.props.strategy || "")
      .toLowerCase()
      .includes("managed money market solution");

  getIarFeeMax = () => (this.isManagedMoneyMarketStrategy() ? 0.15 : 2);

  getFundingThresholdDisplay = () =>
    getIspFundingThreshold(this.props.strategy)?.amount
      ? formatFundingAmount(getIspFundingThreshold(this.props.strategy).amount)
      : "";

  getPathTransitionLead = () => {
    const fundingThreshold = this.getFundingThresholdDisplay();
    return fundingThreshold
      ? `After meeting the ${fundingThreshold} threshold`
      : "For the Path Strategy";
  };

  // The API/configuration is authoritative. Inventing a local schedule while
  // it is loading (or after an error) can expose another office's breakpoints.
  getFeeSchedules = () => getIarFeeSchedules(this.props.customFees);
  getFeeScheduleRows = () => getIarFeeScheduleRows(this.props.customFees);

  renderFeeTable = (tieredRates, title, subtitle) => (
    <div className="form-group mt-3">
      <label>
        <strong>{title}</strong>{" "}
        {subtitle && (
          <span style={{ fontSize: "12px", color: "#6c757d" }}>
            ({subtitle})
          </span>
        )}
      </label>
      {/* Scrollable wrapper keeps a long tier table from pushing the rest of
          Section 4 below the fold; user can scroll within the frame. No border
          here — the table's own `table-bordered` is the single visible box, so
          a short (non-scrolling) table doesn't show a double border. */}
      <div
        style={{
          maxHeight: "320px",
          overflowY: "auto",
          maxWidth: "452px",
          width: "fit-content",
        }}
      >
        <table
          className="table table-sm table-bordered mb-0"
          style={{ maxWidth: "450px" }}
        >
          <thead
            style={{
              position: "sticky",
              top: 0,
              backgroundColor: "#fff",
              zIndex: 1,
            }}
          >
            <tr>
              <th>Tier</th>
              <th style={{ width: "120px" }}>Rate (%)</th>
            </tr>
          </thead>
          <tbody>
            {tieredRates.map((tier, index) => (
              <tr key={index}>
                <td style={{ verticalAlign: "middle" }}>{tier.label}</td>
                <td
                  style={{
                    backgroundColor: "#f8f9fa",
                    color: "#495057",
                    verticalAlign: "middle",
                  }}
                >
                  {Number(tier.rate).toFixed(2)}%
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  renderFlatFeeTable = () => {
    const flatFee = this.props.managementFees?.flatFee;
    if (flatFee === undefined || flatFee === null || flatFee === "")
      return null;
    return this.renderFeeTable(
      [{ label: "All assets", rate: Number(flatFee) }],
      "Fixed Percentage - All Assets",
      "Program Fee — Read Only",
    );
  };

  renderStandardFees = () => {
    if (this.props.managementFeesLoading) {
      return (
        <div
          className="form-group mt-3 text-center"
          style={{ maxWidth: "450px" }}
        >
          <div
            className="spinner-border spinner-border-sm text-primary"
            role="status"
          >
            <span className="sr-only">Loading...</span>
          </div>
          <span className="ml-2" style={{ fontSize: "13px", color: "#6c757d" }}>
            Loading fee schedule...
          </span>
        </div>
      );
    }
    const fees = this.props.managementFees;
    const flatTable = this.renderFlatFeeTable();
    const hasTiers = fees?.tieredRates?.length > 0;
    // Path mode: a flat-fee strategy whose path target's row
    // was injected into fees by ispForm.fetchManagementFees. Signalled by the
    // pathMinimumFee carry-along, never set in any other path.
    const pathMin = fees?.pathMinimumFee;
    const inPathMode = flatTable != null && hasTiers && pathMin != null;
    const pathTransitionLead = this.getPathTransitionLead();
    const noteStyle = {
      fontSize: "12px",
      color: "#6c757d",
      fontWeight: 700,
      marginTop: "-8px",
    };

    if (flatTable || hasTiers) {
      return (
        <div>
          {/* Fixed percentage renders first — it applies now, before any
              path event. Mirrors the generated document's section order. */}
          {flatTable}
          {flatTable && fees?.minimumFee && (
            <div style={noteStyle}>
              Minimum account fee of ${fees.minimumFee} applies to the Fixed
              Percentage. The greater of the minimum or the Fixed Percentage
              will be assessed.
            </div>
          )}
          {inPathMode && (
            <div
              style={{
                fontSize: "12px",
                color: "#6c757d",
                fontStyle: "italic",
                marginTop: "8px",
                marginBottom: "8px",
              }}
            >
              {pathTransitionLead}
              {this.getFundingThresholdDisplay()
                ? " (as described in the Strategy Description),"
                : ","}{" "}
              the Investment Management Fee converts to the tiered schedule shown below.
            </div>
          )}
          {hasTiers &&
            this.renderFeeTable(
              fees.tieredRates,
              "Investment Management Fee",
              "Program Fee — Read Only",
            )}
          {hasTiers && inPathMode && (
            <div style={noteStyle}>
              {pathTransitionLead}, a minimum account fee of ${pathMin} applies. The
              greater of the minimum account fee or the
              Investment Management Fee percentage will be assessed.
            </div>
          )}
          {hasTiers && !inPathMode && !flatTable && fees?.minimumFee && (
            <div style={noteStyle}>
              Minimum account fee of ${fees.minimumFee} applies. The greater of
              the minimum account fee or the Investment Management Fee
              percentage will be assessed.
            </div>
          )}
        </div>
      );
    }
    return (
      <div
        className="form-group mt-3"
        style={{ borderLeft: "5px solid #dc3545", paddingLeft: "12px" }}
      >
        <label>
          <strong>Investment Management Fee</strong>
        </label>
        <div style={{ fontSize: "13px", color: "#dc3545" }}>
          No fee schedule found for this strategy and tier combination. Please
          verify the selected tier is correct.
        </div>
      </div>
    );
  };

  renderFusionFees = () => {
    const allocations = this.props.fusionAllocations || [];
    const total = allocations.reduce(
      (sum, allocation) => sum + (parseFloat(allocation.percentage) || 0),
      0,
    );
    const isComplete = Math.abs(total - 100) < 0.01 && allocations.length > 0;

    return (
      <div className="form-group mt-3">
        {/* T4 (round 4) — show every sub-strategy's full fee schedule
            individually instead of a blended/weighted-average table.
            Stakeholder requirement: every sleeve's tier schedule must
            appear on the ISP for compliance, since households can change
            mid-application.

            The outer container keeps a *stable* height (fixed maxHeight,
            generous minHeight) whether we're showing the loading spinner or
            the loaded tables, so the async sub-strategy fee fetch finishing
            doesn't shove the IAR Fee section below it down the page and
            yank the cursor away from whatever the user is typing. */}
        {isComplete &&
          (this.props.fusionSubFeesLoading ||
            this.props.fusionFeeBlocks?.length > 0) && (
            <div>
              <label className="d-block mt-3 mb-1">
                <strong>
                  Sub-Strategy Investment Management Fee Schedules
                </strong>
                <small className="text-muted ml-2">
                  (per sub-strategy — Read Only)
                </small>
              </label>
              <div
                style={{
                  minHeight: "220px",
                  maxHeight: "420px",
                  overflowY: "auto",
                  border: "1px solid #dee2e6",
                  borderRadius: "4px",
                  padding: "12px 14px",
                  backgroundColor: "#fcfcfd",
                }}
              >
                {this.props.fusionSubFeesLoading ? (
                  <div className="text-center" style={{ padding: "32px 0" }}>
                    <div
                      className="spinner-border spinner-border-sm text-primary"
                      role="status"
                    />
                    <span
                      className="ml-2"
                      style={{ fontSize: "13px", color: "#6c757d" }}
                    >
                      Loading sub-strategy fee schedules...
                    </span>
                  </div>
                ) : (
                  this.props.fusionFeeBlocks.map((block, idx) => (
                    <div key={idx} className="mb-3">
                      <div
                        style={{
                          fontWeight: 600,
                          color: "#385EA4",
                          marginBottom: "4px",
                        }}
                      >
                        {block.displayName || block.strategy}
                        <span
                          style={{
                            fontWeight: 400,
                            color: "#6c757d",
                            marginLeft: "8px",
                          }}
                        >
                          {block.weight !== "" &&
                          block.weight !== null &&
                          block.weight !== undefined
                            ? `Allocation: ${Number(block.weight).toFixed(0)}%`
                            : ""}
                          {block.tier && block.tier !== "Standard"
                            ? `${block.weight !== "" ? "   •   " : ""}Tier: ${block.tier}`
                            : ""}
                        </span>
                      </div>
                      {block.tieredRates?.length > 0 ? (
                        <table
                          className="table table-sm table-bordered mb-1"
                          style={{ maxWidth: "450px" }}
                        >
                          <thead>
                            <tr>
                              <th>Asset Level</th>
                              <th style={{ width: "120px" }}>Annual Fee</th>
                            </tr>
                          </thead>
                          <tbody>
                            {block.tieredRates.map((tier, i) => (
                              <tr key={i}>
                                <td style={{ verticalAlign: "middle" }}>
                                  {tier.label}
                                </td>
                                <td
                                  style={{
                                    backgroundColor: "#f8f9fa",
                                    color: "#495057",
                                    verticalAlign: "middle",
                                  }}
                                >
                                  {Number(tier.rate).toFixed(2)}%
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      ) : (
                        <div style={{ fontSize: "12px", color: "#dc3545" }}>
                          No tier schedule found for this sub-strategy at the
                          selected tier.
                        </div>
                      )}
                      {block.minimumFee && (
                        <div
                          style={{
                            fontSize: "12px",
                            color: "#6c757d",
                            fontStyle: "italic",
                          }}
                        >
                          *Minimum account fee of ${block.minimumFee} applies
                          for this sub-strategy.
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        {!this.props.fusionSubFeesLoading &&
          this.props.fusionFeeValidation?.issues?.length > 0 && (
            <div
              className="mt-3"
              style={{
                borderLeft: "5px solid #dc3545",
                paddingLeft: "12px",
                maxWidth: "700px",
              }}
            >
              <label>
                <strong>Fusion Management Fee Data Issue</strong>
              </label>
              <div style={{ fontSize: "13px", color: "#dc3545" }}>
                One or more selected sub-strategies is missing a management fee
                schedule for the selected tier. Each sub-strategy must have its
                own complete tier schedule before this section can be submitted.
              </div>
              <div
                style={{ fontSize: "13px", color: "#dc3545", marginTop: "6px" }}
              >
                {this.props.fusionFeeValidation.issues
                  .map((issue) => issue.message)
                  .join(" ")}{" "}
                If the issue persists after retrying, contact support before
                completing Section 4.
              </div>
            </div>
          )}
      </div>
    );
  };

  renderFeeStageHeading = (text) => (
    <div
      className="isp-fee-stage-heading mt-3 mb-3 px-3 py-2"
      style={{
        backgroundColor: "#385EA4",
        color: "#fff",
        fontWeight: 700,
        textTransform: "uppercase",
        fontSize: "14px",
      }}
    >
      {text}
    </div>
  );

  renderFeeSubheading = (text) => (
    <div
      className="isp-fee-subheading form-group mb-2 px-3 py-2"
      style={{
        backgroundColor: "#e9ecef",
        color: "#343a40",
        fontWeight: 700,
        fontSize: "14px",
      }}
    >
      {text}
    </div>
  );

  renderIarFlatInput = ({
    value,
    onChange,
    borderComplete,
    inputId = "iarFeeFlat-initial",
  }) => {
    const max = this.getIarFeeMax();
    return (
      <div className="form-group mb-0">
        <label htmlFor={inputId}>
          <strong>Fixed Percentage Rate</strong>
        </label>
        <div className="input-group" style={{ maxWidth: "200px" }}>
          <input
            type="number"
            id={inputId}
            className="form-control isp-iar-fee-input"
            style={{
              borderLeftColor: borderComplete ? "green" : "#ced4da",
              borderLeftWidth: "5px",
            }}
            value={value}
            onChange={(e) => {
              const val = e.target.value;
              if (
                val === "" ||
                (/^(?:0|[1-9]\d*)?(?:\.\d{0,2})?$/.test(val) &&
                  Number(val) <= max)
              ) {
                onChange(val);
              }
            }}
            onBlur={(e) => {
              const val = e.target.value;
              if (val !== "" && !isNaN(Number(val)))
                onChange(formatIarFeeRate(val));
            }}
            placeholder="0.00"
            step="0.01"
            min="0"
            max={max}
          />
          <div className="input-group-append">
            <span className="input-group-text">%</span>
          </div>
        </div>
        {max < 2 && (
          <small className="form-text text-muted">
            Maximum IAR fee for Managed Money Market Solutions is {max}%.
          </small>
        )}
      </div>
    );
  };

  renderIarFeeBlock = ({
    type,
    flat,
    tiered,
    schedule,
    onType,
    onFlat,
    onTiered,
    onSchedule,
    feeSchedules,
    feeScheduleRows,
    scheduleNames,
    disabled = false,
    radioGroupName = "iarFeeType",
    radioIdPrefix = "feeType",
    flatInputId = "iarFeeFlat",
    scheduleInputId = "iarFeeScheduleType",
    allowFlatFee = false,
  }) => {
    const max = this.getIarFeeMax();
    const currentScheduleLabels = feeSchedules[schedule] || [];
    const feeScheduleStatus = this.props.customFeesLoading
      ? "Loading authorized IAR fee schedules..."
      : this.props.customFeesError ||
        "No IAR fee schedule is configured for this advisor and strategy.";
    const iarFeeTypeComplete =
      (type === "Fixed Percentage" &&
        isValidIarPercentage(flat, { max })) ||
      (type === "Tiered" &&
        currentScheduleLabels.length > 0 &&
        normalizeIarFeeTiered(tiered, currentScheduleLabels).every(
          (t) => t.rate !== "",
        )) ||
      (type === "Flat Fee" && /^\d+(?:\.\d{1,2})?$/.test(String(flat || "")));

    return (
      <div className="mb-3">
        <div className="form-group">
          <label>
            <strong>IAR Fee Type</strong>
          </label>
          <div
            style={{
              borderLeft: `5px solid ${iarFeeTypeComplete ? "green" : "#ced4da"}`,
              paddingLeft: "12px",
            }}
          >
            <div className="form-check form-check-inline">
              <input
                className="form-check-input"
                type="radio"
                name={radioGroupName}
                id={`${radioIdPrefix}Flat`}
                value="Fixed Percentage"
                checked={type === "Fixed Percentage"}
                disabled={disabled}
                onChange={(e) => onType(e.target.value)}
                style={{ height: "15px", width: "15px" }}
              />
              <label
                className="form-check-label"
                htmlFor={`${radioIdPrefix}Flat`}
              >
                Fixed Percentage
              </label>
            </div>
            <div className="form-check form-check-inline">
              <input
                className="form-check-input"
                type="radio"
                name={radioGroupName}
                id={`${radioIdPrefix}Tiered`}
                value="Tiered"
                checked={type === "Tiered"}
                disabled={disabled || scheduleNames.length === 0}
                onChange={(e) => onType(e.target.value)}
                style={{ height: "15px", width: "15px" }}
              />
              <label
                className="form-check-label"
                htmlFor={`${radioIdPrefix}Tiered`}
              >
                Tiered
              </label>
            </div>
            {allowFlatFee && (
              <div className="form-check form-check-inline">
                <input
                  className="form-check-input"
                  type="radio"
                  name={radioGroupName}
                  id={`${radioIdPrefix}DollarFlat`}
                  value="Flat Fee"
                  checked={type === "Flat Fee"}
                  disabled={disabled}
                  onChange={(e) => onType(e.target.value)}
                />
                <label
                  className="form-check-label isp-iar-fee-type-label"
                  htmlFor={`${radioIdPrefix}DollarFlat`}
                >
                  Flat Fee
                </label>
              </div>
            )}
            {!disabled && scheduleNames.length === 0 && (
              <small className="form-text text-muted" role="status">
                {feeScheduleStatus}
              </small>
            )}
          </div>
        </div>

        {/* Fixed Percentage Fee */}
        {type === "Fixed Percentage" && (
          <div className="form-group mb-0">
            <label htmlFor={flatInputId}>
              <strong>Fixed Percentage Rate</strong>
            </label>
            <div className="input-group" style={{ maxWidth: "200px" }}>
              <input
                type="number"
                id={flatInputId}
                className="form-control isp-iar-fee-input"
                style={{
                  borderLeftColor: isValidIarPercentage(flat, { max })
                    ? "green"
                    : "#ced4da",
                  borderLeftWidth: "5px",
                }}
                value={flat}
                onChange={(e) => {
                  const val = e.target.value;
                  if (
                    val === "" ||
                    (/^(?:0|[1-9]\d*)?(?:\.\d{0,2})?$/.test(val) &&
                      Number(val) <= max)
                  ) {
                    onFlat(val);
                  }
                }}
                onBlur={(e) => {
                  const val = e.target.value;
                  if (val !== "" && !isNaN(Number(val))) {
                    onFlat(formatIarFeeRate(val));
                  }
                }}
                placeholder="0.00"
                step="0.01"
                min="0"
                max={max}
              />
              <div className="input-group-append">
                <span className="input-group-text">%</span>
              </div>
            </div>
            {max < 2 && (
              <small className="form-text text-muted">
                Maximum IAR fee for Managed Money Market Solutions is {max}%.
              </small>
            )}
          </div>
        )}

        {type === "Flat Fee" && (
          <div className="form-group mb-0">
            <label htmlFor={flatInputId}><strong>Flat Fee Amount</strong></label>
            <div className="input-group" style={{ maxWidth: "240px" }}>
              <div className="input-group-prepend"><span className="input-group-text">$</span></div>
              <input
                type="number"
                id={flatInputId}
                className="form-control isp-iar-fee-input"
                value={flat}
                min="0"
                step="0.01"
                placeholder="0.00"
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === "" || /^\d*(?:\.\d{0,2})?$/.test(val)) onFlat(val);
                }}
                onBlur={(e) => {
                  if (e.target.value !== "") onFlat(Number(e.target.value).toFixed(2));
                }}
              />
            </div>
          </div>
        )}

        {/* Tiered Fee */}
        {type === "Tiered" &&
          (() => {
            const tieredRates = normalizeIarFeeTiered(
              tiered,
              currentScheduleLabels,
            );
            return (
              <div className="form-group mb-0">
                {scheduleNames.length > 1 && (
                  <div className="form-group">
                    <label htmlFor={scheduleInputId}>
                      <strong>Breakpoint Schedule</strong>
                    </label>
                    <select
                      id={scheduleInputId}
                      className="form-control"
                      value={schedule}
                      onChange={(e) => {
                        const newScheduleLabels =
                          feeSchedules[e.target.value] || [];
                        const newScheduleRows =
                          feeScheduleRows[e.target.value] || [];
                        onTiered(
                          normalizeIarFeeTiered(
                            newScheduleRows,
                            newScheduleLabels,
                          ),
                        );
                        if (onSchedule) onSchedule(e.target.value);
                      }}
                    >
                      <option value="">Select Breakpoint Schedule</option>
                      {scheduleNames.map((name, index) => (
                        <option value={name} key={index}>
                          {name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {currentScheduleLabels.length > 0 ? (
                  <>
                    <label>
                      <strong>Tiered Fee Schedule</strong>
                    </label>
                    <table
                      className="table table-sm table-bordered"
                      style={{ maxWidth: "450px" }}
                    >
                      <thead>
                        <tr>
                          <th>Tier</th>
                          <th style={{ width: "120px" }}>Rate (%)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentScheduleLabels.map((label, index) => {
                      const rowRate = tieredRates[index]
                        ? tieredRates[index].rate
                        : "";
                      return (
                        <tr key={index}>
                          <td style={{ verticalAlign: "middle" }}>{label}</td>
                          <td>
                            <div className="input-group input-group-sm">
                              <input
                                type="number"
                                className="form-control isp-iar-fee-input"
                                aria-label={`${label} IAR fee rate`}
                                value={rowRate}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  if (
                                    val === "" ||
                                    (/^(?:0|[1-9]\d*)?(?:\.\d{0,2})?$/.test(
                                      val,
                                    ) &&
                                      Number(val) <= max)
                                  ) {
                                    onTiered(
                                      setTieredRate(
                                        tiered,
                                        currentScheduleLabels,
                                        index,
                                        val,
                                      ),
                                    );
                                  }
                                }}
                                onBlur={(e) => {
                                  const val = e.target.value;
                                  if (val !== "" && !isNaN(Number(val))) {
                                    onTiered(
                                      setTieredRate(
                                        tiered,
                                        currentScheduleLabels,
                                        index,
                                        formatIarFeeRate(val),
                                      ),
                                    );
                                  }
                                }}
                                placeholder="0.00"
                                step="0.01"
                                min="0"
                                max={max}
                              />
                              <div className="input-group-append">
                                <span className="input-group-text">%</span>
                              </div>
                            </div>
                          </td>
                        </tr>
                      );
                        })}
                      </tbody>
                    </table>
                  </>
                ) : (
                  <div className="alert alert-warning py-2 mb-0" role="alert">
                    {scheduleNames.length > 1
                      ? "Select an authorized breakpoint schedule to enter tiered rates."
                      : feeScheduleStatus}
                  </div>
                )}
              </div>
            );
          })()}
      </div>
    );
  };

  renderElementsFees = (feeSchedules, feeScheduleRows, scheduleNames) => {
    if (this.props.managementFeesLoading) return this.renderStandardFees();

    const { values, onChange } = this.props;
    const fees = this.props.managementFees || {};
    const flatTable = this.renderFlatFeeTable();
    const hasTiers = fees?.tieredRates?.length > 0;
    const pathTransitionLead = this.getPathTransitionLead();
    const noteStyle = {
      fontSize: "12px",
      color: "#6c757d",
      fontWeight: 700,
      marginTop: "-8px",
    };

    return (
      <>
        <div data-testid="initial-elements-fee-stage">
          {this.renderFeeStageHeading(ELEMENTS_INITIAL_FEE_STAGE_HEADING)}
          {this.renderFeeSubheading("Investment Management Fee")}
          {flatTable}
          {flatTable && fees?.minimumFee && (
            <div style={noteStyle}>
              {`Minimum account fee of $${fees.minimumFee} applies to the Investment Management Fee. The greater of the minimum account fee or the Investment Management Fee will be assessed.`}
            </div>
          )}

          {this.renderFeeSubheading("IAR Fee")}
          {this.renderIarFeeBlock({
            type: values.iarFeeType || "Fixed Percentage",
            flat: values.iarFeeFlat,
            tiered: values.iarFeeTiered,
            schedule: values.iarFeeScheduleType,
            onType: (v) => onChange("iarFeeType", v),
            onFlat: (v) => onChange("iarFeeFlat", v),
            onTiered: (v) => onChange("iarFeeTiered", v),
            allowFlatFee: this.props.allowFlatFee,
            onSchedule: (v) => onChange("iarFeeScheduleType", v),
            feeSchedules,
            feeScheduleRows,
            scheduleNames,
            disabled: false,
            radioGroupName: "initialIarFeeType",
            radioIdPrefix: "initialFeeType",
            flatInputId: "iarFeeFlat-initial",
            scheduleInputId: "initialIarFeeScheduleType",
          })}
        </div>

        {this.props.hasPath && (
          <div data-testid="path-elements-fee-stage">
            {this.renderFeeStageHeading(
              getElementsPathFeeStageHeading(this.props.pathStrategy),
            )}
            {this.renderFeeSubheading("Investment Management Fee")}
            {hasTiers && (
              <>
                <div
                  style={{
                    fontSize: "12px",
                    color: "#6c757d",
                    fontStyle: "italic",
                    marginTop: "8px",
                    marginBottom: "8px",
                  }}
                >
                  {pathTransitionLead}
                  {this.getFundingThresholdDisplay()
                    ? " (as described in the Strategy Description),"
                    : ","}{" "}
                  the Investment Management Fee converts to the tiered schedule shown below.
                </div>
                {this.renderFeeTable(
                  fees.tieredRates,
                  "Fee Schedule",
                  "Program Fee - Read Only",
                )}
              </>
            )}
            {hasTiers && fees?.pathMinimumFee && (
              <div style={noteStyle}>
                {`${pathTransitionLead}, a minimum account fee of $${fees.pathMinimumFee} applies. The greater of the minimum account fee or the Investment Management Fee percentage will be assessed.`}
              </div>
            )}

            {this.renderFeeSubheading("IAR Fee")}
            {this.renderIarFeeBlock({
              type: values.pathIarFeeType,
              flat: values.pathIarFeeFlat,
              tiered: values.pathIarFeeTiered,
              schedule: values.iarFeeScheduleType,
              onType: (v) => onChange("pathIarFeeType", v),
              onFlat: (v) => onChange("pathIarFeeFlat", v),
              onTiered: (v) => onChange("pathIarFeeTiered", v),
              onSchedule: (v) => onChange("iarFeeScheduleType", v),
              feeSchedules,
              feeScheduleRows,
              scheduleNames,
              disabled: false,
              allowFlatFee: this.props.allowFlatFee,
              radioGroupName: "pathIarFeeType",
              radioIdPrefix: "pathFeeType",
              flatInputId: "pathIarFeeFlat",
              scheduleInputId: "pathIarFeeScheduleType",
            })}
          </div>
        )}
      </>
    );
  };

  render() {
    const { values, onChange } = this.props;

    const tierOptions = this.props.managementFeeTierOptions || [];
    const feeSchedules = this.getFeeSchedules();
    const feeScheduleRows = this.getFeeScheduleRows();
    const scheduleNames = Object.keys(feeSchedules);
    const mgmt = this.props.managementFees;
    const isFlat = !!(mgmt && mgmt.flatFee != null);
    const hasPath = !!this.props.hasPath;
    const isElementsFeeMode =
      isFlat &&
      (this.props.hasInitialFeeStage || isElementsStrategyName(this.props.strategy));

    // Only show dropdown if there are 2+ tiers to choose from
    const showTierDropdown = tierOptions.length > 1;
    // The "Investment Management Fee Tier" subsection is hidden only when
    // there is nothing to choose: Standard is the sole available tier. This
    // is driven by the user's Evolution tiers and applies equally to Fusion,
    // which still resolves a tier even though its fee schedules are shown
    // per-sub-strategy.
    const hideTierSection =
      this.isManagedMoneyMarketStrategy() ||
      (tierOptions.length === 1 && tierOptions[0].label === "Standard");
    const tierHelperText = !this.props.strategy
      ? "Please select a strategy in Section 2 to see available fee tiers."
      : tierOptions.length === 0
        ? "No fee tiers are available for the selected strategy and your Evolution tiers."
        : null;
    const feeLookupFailed =
      !!this.props.customFeesError ||
      (this.props.isFusion
        ? !this.props.fusionSubFeesLoading &&
          this.props.fusionFeeValidation?.issues?.length > 0
        : !!values.iarFeeTier &&
          !this.props.managementFeesLoading &&
          !this.props.managementFees);

    return (
      <div>
        {!hideTierSection && (
          <div className="form-group">
            <label htmlFor="iarFeeTier">
              <strong>Investment Management Fee Tier</strong>
            </label>
            {tierOptions.length > 0 ? (
              showTierDropdown ? (
                <select
                  id="iarFeeTier"
                  className="form-control"
                  style={{
                    borderLeftColor: values.iarFeeTier ? "green" : "#ced4da",
                    borderLeftWidth: "5px",
                  }}
                  value={values.iarFeeTier}
                  onChange={(e) => onChange("iarFeeTier", e.target.value)}
                >
                  <option value="">Select Fee Tier</option>
                  {tierOptions.map((option) => (
                    <option value={option.label} key={option.exactTier}>
                      {option.label}
                    </option>
                  ))}
                </select>
              ) : (
                <div style={{ fontSize: "15px", color: "#495057" }}>
                  {tierOptions[0].label}
                </div>
              )
            ) : (
              <div
                style={{
                  fontSize: "13px",
                  color: this.props.strategy ? "#6c757d" : "#495057",
                  borderLeft: "5px solid #ced4da",
                  paddingLeft: "12px",
                }}
              >
                {tierHelperText}
              </div>
            )}
          </div>
        )}

        {/* Investment Management Fee — standard or Fusion sub-strategy
            schedules. Both wait for a resolved fee tier: when the subsection
            is hidden (Standard-only) iarFeeTier is auto-set, so this renders
            immediately; when a tier dropdown is shown it waits for a pick. */}
        {this.props.strategy && feeLookupFailed && this.props.onRetryFeeSchedules && (
          <button
            type="button"
            className="btn btn-outline-primary btn-sm mb-3"
            onClick={this.props.onRetryFeeSchedules}
          >
            Retry fee schedules
          </button>
        )}

        {!this.props.hideInvestmentManagementFees &&
          this.props.strategy &&
          values.iarFeeTier &&
          (this.props.isFusion
            ? this.renderFusionFees()
            : isElementsFeeMode
              ? this.renderElementsFees(
                  feeSchedules,
                  feeScheduleRows,
                  scheduleNames,
                )
              : this.renderStandardFees())}

        {!isElementsFeeMode && (
          <>
            {/* Divider: investment management fee above, IAR fee below */}
            <hr style={{ borderTop: "1px solid #dee2e6", margin: "20px 0" }} />

            {/* IAR Fee — three modes:
            1. normal (no flatFee): one block with Flat/Tiered radio (unchanged).
            2. flat IM + no path (MMMS-shaped): radio disabled, flat only.
            3. flat IM + path (Elements-shaped): two stacked sub-blocks with
               stage headers; initial has no radio; path has the full radio. */}
            {(() => {
              if (isFlat && !hasPath) {
                return this.renderIarFeeBlock({
                  type: "Fixed Percentage",
                  flat: values.iarFeeFlat,
                  tiered: [],
                  schedule: values.iarFeeScheduleType,
                  onType: () => {},
                  onFlat: (v) => onChange("iarFeeFlat", v),
                  onTiered: () => {},
                  onSchedule: (v) => onChange("iarFeeScheduleType", v),
                  feeSchedules,
                  feeScheduleRows,
                  scheduleNames,
                  disabled: true,
                });
              }
              return this.renderIarFeeBlock({
                type: values.iarFeeType,
                flat: values.iarFeeFlat,
                tiered: values.iarFeeTiered,
                schedule: values.iarFeeScheduleType,
                onType: (v) => onChange("iarFeeType", v),
                onFlat: (v) => onChange("iarFeeFlat", v),
                onTiered: (v) => onChange("iarFeeTiered", v),
                onSchedule: (v) => onChange("iarFeeScheduleType", v),
                feeSchedules,
                feeScheduleRows,
                scheduleNames,
                disabled: false,
                allowFlatFee: this.props.allowFlatFee,
              });
            })()}
          </>
        )}

        {/* Zero fee waiver fine print (shown after user confirmed) */}
        {values.iarFeeWaiver && (
          <div
            className="mt-2"
            style={{ fontSize: "11px", color: "#6c757d", fontStyle: "italic" }}
          >
            Fee waiver acknowledged on {values.iarFeeWaiver.confirmedAt} by{" "}
            {values.iarFeeWaiver.confirmedBy}. Zero-rate fields:{" "}
            {values.iarFeeWaiver.zeroFields.join(", ")}.
          </div>
        )}
      </div>
    );
  }
}

export default ISPFeeSchedule;
